import { useState, useEffect } from "react";
import { ChevronRight, AlertTriangle, CloudLightning } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getAllSeasons } from "@/lib/stormsQueries";

function SkeletonRow() {
  return (
    <div className="animate-pulse flex gap-4 px-4 py-3 border-b border-white/5">
      <div className="h-4 w-24 bg-[#1e2433]" />
      <div className="h-4 w-12 bg-[#1e2433]" />
      <div className="h-4 w-32 bg-[#1e2433]" />
      <div className="h-4 w-32 bg-[#1e2433]" />
    </div>
  );
}

function fmt(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function CustomBarTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0d1117] border border-white/10 px-3 py-2 text-xs">
        <p className="text-[#F9FAFB] font-medium">{label} Season</p>
        <p className="text-cyan-400">{payload[0].value} storm{payload[0].value !== 1 ? "s" : ""}</p>
      </div>
    );
  }
  return null;
}

export default function SeasonsPage({ onSelectSeason }) {
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getAllSeasons()
      .then(setSeasons)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (error) {
    return (
      <div className="border border-red-900/40 bg-[#1a0a0a] p-4 flex items-start gap-3 text-red-400">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
        <p className="text-sm">Failed to load seasons: {error}</p>
      </div>
    );
  }

  // Bar chart data — oldest season first for left-to-right chronological order
  const chartData = [...seasons].reverse().map((s) => ({
    season: s.season,
    count: s.storm_count ?? 0,
  }));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-[#F9FAFB]">All Seasons</h2>
        <p className="text-sm text-[#6B7280] mt-1">Click a season to view individual storms</p>
      </div>

      {/* Bar chart */}
      {!loading && seasons.length > 0 && (
        <div className="border border-white/10 bg-[#111827] p-6">
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#F9FAFB] mb-1">Storms per Season</h3>
          <p className="text-xs text-[#6B7280] mb-4">2015/16 → current</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={chartData}
              margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="season" tick={{ fill: "#6B7280", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<CustomBarTooltip />} cursor={{ fill: "rgba(6,182,212,0.08)" }} />
              <Bar dataKey="count" fill="#3b82f6" radius={[0, 0, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Table */}
      <div className="border border-white/10 bg-[#111827] overflow-hidden">
        {/* Table header */}
        <div className="grid grid-cols-[2fr_1fr_2fr_2fr_auto] gap-4 px-4 py-3 border-b border-white/10 text-xs uppercase tracking-wider text-[#6B7280]">
          <span>Season</span>
          <span>Storms</span>
          <span>First Named</span>
          <span className="hidden sm:block">Latest Named</span>
          <span />
        </div>

        {loading ? (
          [...Array(5)].map((_, i) => <SkeletonRow key={i} />)
        ) : seasons.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-[#6B7280]">
            <CloudLightning className="w-10 h-10 opacity-30" />
            <p className="text-sm">No season data available yet.</p>
          </div>
        ) : (
          seasons.map((s) => (
            <button
              key={s.season}
              onClick={() => onSelectSeason && onSelectSeason(s.season)}
              className="w-full grid grid-cols-[2fr_1fr_2fr_2fr_auto] gap-4 px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors text-left group"
            >
              <span className="text-sm font-medium text-cyan-400 group-hover:text-cyan-300">
                {s.season}
              </span>
              <span className="text-sm text-[#F9FAFB]">{s.storm_count ?? 0}</span>
              <span className="text-sm text-[#9CA3AF]">{fmt(s.earliest_named)}</span>
              <span className="hidden sm:block text-sm text-[#9CA3AF]">{fmt(s.latest_named)}</span>
              <ChevronRight className="w-4 h-4 text-[#6B7280] group-hover:text-cyan-400 transition-colors self-center" />
            </button>
          ))
        )}
      </div>
    </div>
  );
}

